<template>
  <div class="my-6 leading-normal">
    <div v-if="selectedOffice.role == 'Legacy'" class="rounded-lg bg-white relative w-full leading-normal p-8">
      <p class="mb-2 text-sm">Our automated referral system is available to members on the Standard, Enhanced and Premium plans.</p>
      <p class="mb-6 text-sm"> To access this service you will need to upgrade to one of these plans.</p>
      <p class="mb-4 text-sm">Upgrading is simple - follow the link below to learn more!</p>
      <button class="bg-tertiary text-white rounded px-4 py-3 uppercase text-sm font-medium align-bottom ml-auto w-1/4">
        <router-link to="/guild-plan" tag="button" class="text-white">Upgrade My Plan</router-link>
      </button>
    </div>
    <div v-else-if="selectedOffice.automated_referrals_permission !== true" class="rounded-lg bg-white relative w-full leading-normal p-8">
      <p class="mb-6 text-sm">
        Save time and increase referral income by signing up to the automated referral system. The system will suggest new referrals daily and will automatically update the status of your referrals. Why wait?
      </p>
      <button v-if="registrationLink" @click="openLink(registrationLink)" type="button" class="py-3 px-6 w-48 bg-tertiary text-white rounded mr-4">Sign up now</button>
    </div>

    <loader v-else-if="loading" />

    <div v-else class="rounded-lg bg-white relative w-full leading-normal p-8">
      <h3 class="mb-6 text-xl text-grey-darkest"><span>Automatic Referral System – Potential Referrals</span></h3>
      <p class="mb-6 text-sm" >The tabs below show a breakdown of your automatically detected referral opportunities, based on applicants that you have recently added to your database (CRM system). Applicants listed below are identified as 'Out of Area' referral opportunities, based on potentially having a property to sell, outside of your usual area of coverage.</p>
      <p class="mb-6 text-sm">
        <strong>'Action Required'</strong> - In the first tab, the leads are listed for you to action ASAP, either by gaining consent or choosing to decline/not to make the referral. In order for them to be referred to another agent, consent must be obtained from the applicant, either verbally or in writing, that they are happy to be contacted by members of the network in regard to the sale of their property.
      </p>
      <p class="mb-6 text-sm"><strong>'Archived'</strong> - After 14 days, if no action has been taken (ie; you have not clicked that you have had consent or alternatively declined the opportunity for the referral to be made) the referral opportunity will automatically move to sit under the 'Archived' tab. You can still action this here, at a later date, should you choose to. All declined leads will also automatically move to sit under the 'Archived' tab, once selected.</p>
      <div class="w-full mb-6 flex flex-wrap text-xs border-b border-grey-darker">
          <button @click="referralType = 'current'" class="p-3 bg-grey-light focus:outline-none rounded-sm" :class="{'bg-white border-grey-darker border-r border-t tab-margin-bottom': referralType === 'current'}">
            <span class="mr-2">{{ selectedOffice.enable_open_automated_referrals ? 'Recent' : 'Action Required' }}</span>
            <span class="text-center text-xxs rounded-full bg-red-dark text-white h-4 w-4 shadow" style="padding: 3px; text-align: center; display: inline-block;">
              {{this.actionRequiredReferralCount}}
            </span>
          </button>
          <button @click="referralType = 'expired'" class="p-3 bg-grey-light focus:outline-none  rounded-sm" :class="{'bg-white border-grey-darker border-l border-r border-t tab-margin-bottom': referralType === 'expired'}">
            <span class="mr-2">Archived</span>
          </button>
          <button @click="referralType = 'settings'" class="p-3 bg-grey-light focus:outline-none  rounded-sm" :class="{'bg-white border-grey-darker border-l border-r border-t tab-margin-bottom': referralType === 'settings'}">
            <span class="mr-2">Settings</span>
          </button>
        </div>
       <div v-if="referralType === 'settings'" class="bg-grey-lighter border mb-6 p-4 text-sm">
        <p>Within your CRM data, each applicant will have ‘positions’ or similarly named properties to indicate their market position and business interest. Use the below filter controls to specify groups of positions to include or exclude when your Automatic Referral leads are synchronised.</p>
        <p class="mt-4">Use the <strong>Add custom position</strong> actions to specify positions to include or exclude. Use the <strong>Add filter group</strong> actions to group positions for additional filtering. You also have the option to use a preset of position filters readily made for a number of CRM providers.</p>
        <div class="flex flex-wrap -mx-2 items-stretch mt-4">
          <div class="flex-1 w-full px-2">
            <panel withHeader class="h-full">
              <div class="flex flex-wrap">
              <div class="flex-1 w-full lg:w-1/4 px-2">
              <h1 slot="header" class="border-grey-lighter w-full pb-3 text-lg">
                Apply Filter Preset
              </h1>
            </div>
           <div class="flex-1 w-full lg:w-1/2 px-2">
              <p>You can apply a preset of default filters made for a number of CRM providers using the following selection tool.</p>
           </div>
           <div class="flex-1 w-full lg:w-1/4 px-2">
              <span v-if="showCrmProviderText" class="font-bold">
                {{ selectedCrmProviderName || 'Custom Filters' }} <br>
                <a href="#" @click.prevent="rePresentDropdown" class="text-blue">
                 Select a different preset
                </a>
              </span>
              <select v-else v-model="selectedCrmProvider" class="w-full border focus:border-grey-darkest p-2"  @change="confirmCrmProviders = selectedCrmProvider !== null">
                <option :value="null">Select your CRM Provider</option>
                <option v-for="(crmProvider, i) in crmProviders" :key="i" :value="crmProvider.id" v-if="crmProvider.id !== 5">
                  {{ crmProvider.name }}
                </option>
              </select>
           </div>
         </div>
            </panel>
          </div>
        </div>
        <div class="flex flex-wrap -mx-2 items-stretch mt-4">
          <div class="flex-1 w-full lg:w-1/2 px-2">
            <panel withHeader class="h-full">
             <h1 slot="header" class="border-b border-grey-lighter w-full pb-3 text-lg">
               Applicant Selling Positions:
             </h1>
             <div class="flex flex-wrap -mx-2">
                <referral-applicant-filters ref="sellingRef" typeId="1"
                :filterGroups="sellingFilters"
                @updatereferralApplicantFilters="saveSellingFilterData" />
             </div>
            </panel>
          </div>
          <div class="flex-1 w-full lg:w-1/2 px-2">
            <panel withHeader class="h-full">
              <h1 slot="header" class="border-b border-grey-lighter w-full pb-3 text-lg">
                Applicant Buying Positions:
              </h1>
              <div class="flex flex-wrap -mx-2">
                <referral-applicant-filters ref="buyingRef" typeId="2"
                :filterGroups="buyingFilters" @updatereferralApplicantFilters="saveBuyingFilterData" />
              </div>
            </panel>
          </div>
        </div>
        <div class="flex flex-wrap -mx-2 items-stretch mt-4">
          <div class="flex-1 w-full lg:w-1/2 px-2">
            <panel withHeader class="h-full">
              <h1 slot="header" class="border-b border-grey-lighter w-full pb-3 text-lg">
                Applicant Buying Reasons:
              </h1>
              <div class="flex flex-wrap -mx-2">
                <referral-applicant-filters ref="buyingReasonRef" typeId="3" :filterGroups="buyingReasonFilters"
                @updatereferralApplicantFilters="saveBuyingReasonFilterData" />
              </div>
            </panel>
          </div>
          <div class="flex-1 w-full lg:w-1/2 px-2">
          </div>
        </div>
        <div class="flex flex-wrap -mx-2 mt-4 justify-end">
          <button class="bg-primary text-white rounded border border-primary px-3 py-2 ml-3" @click="saveReferralApplicantFilters">
            <loader fill-color="white" v-if="saving" />
            <span v-else><i class="fas fa-save"></i> Save</span>
          </button>
        </div>
      </div>
      <div v-else-if="applicants.length === 0" class="bg-grey-lighter border mb-6 p-4 text-center">
          <p class="mb-2"><i class="fa fa-exclamation-triangle"></i></p>
          <p class="text-sm">There is no applicant data currently available to show</p>
      </div>
      <div v-else>
        <table class="w-full border-separate mb-8" style="border-spacing: 0 4px">
          <thead class="whitespace-no-wrap">
            <tr class="text-xs align-top">
              <th class="py-4 px-1 uppercase">Name</th>
              <th class="py-4 px-1 uppercase">Email</th>
              <th class="py-4 px-1 uppercase">Property</th>
              <th class="py-4 px-1 uppercase" @click="sortBy('created_at')">Received
                <i v-if="sorting['created_at']==='asc'" class="fa fa-sort-down cursor-pointer"></i>
                <i v-else-if="sorting['created_at']==='desc'" class="fa fa-sort-up cursor-pointer"></i>
                <i v-else class="fa fa-sort cursor-pointer"></i>
              </th>
              <th v-if="selectedOffice.enable_open_automated_referrals !== true" class="py-4 px-1 uppercase">Referring Agent</th>
              <th v-if="selectedOffice.enable_open_automated_referrals !== true" colspan="2" class="py-5 uppercase">Consent <i class="tooltip-container fas fa-info-circle text-blue">
                <div class="absolute border border-bg-primary p-2 bg-white shadow z-10 rounded text-xs normal-case mt-1 tooltip-msg pointer-events-none">
                  <span class="text-black font-sans leading-normal font-normal whitespace-normal">Please confirm whether applicants listed below have given consent that they are happy to
                    be contacted by members of the network in regard to the sale of their property</span>
                </div>
              </i></th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="(applicant, index) in applicants" :key="applicant.id" class="bg-grey-lightest text-xs text-center border-b-4 border-white" :class="{'bg-green-lightest': applicant.consent === true, 'bg-red-lightest': applicant.consent === false}">
              <td class="py-4 px-1 border-l-4 border-grey-light" :class="{'border-green-lighter': applicant.consent === true, 'border-red-lighter': applicant.consent === false}">
                <div class="tooltip-container cursor-"> {{ applicant.first_name }} {{ applicant.last_name }}
                  <div class="absolute border border-bg-primary p-2 bg-white shadow z-10 rounded text-xs text-left normal-case mt-1 tooltip-msg pointer-events-none">
                    <p>Internal ID: {{ applicant.id }}</p>
                    <p>External ID: {{ applicant.applicant_id }}</p>
                  </div>
                </div>
              </td>
              <td class="py-4 px-1">{{ applicant.email }}</td>
              <td class="py-4 px-1">{{ applicant.property }}</td>
              <td class="py-4 px-1"><p>{{ applicant.created_at | date }}</p><p class="text-xs">{{ applicant.created_at | time }}</p></td>
              <td v-if="selectedOffice.enable_open_automated_referrals !== true" class="py-4 px-1">
                <select name="referring_agent_id" v-model="applicant.referring_agent_id" :disabled="applicant.consent !== null" class="w-full border focus:border-grey-darkest p-2" :class="{'hover:border-grey-darker' : applicant.consent === null}">
                  <option :value="null">Please select</option>
                  <option v-for="(agent, i) in agents" :key="i" :value="agent.id">
                    {{ agent.name }}
                  </option>
                </select>
              </td>
              <td v-if="selectedOffice.enable_open_automated_referrals !== true" class="py-4 px-1">
                <button v-if="applicant.consent === false" disabled="disabled" class="cursor-default bg-white text-grey-darker border border-red-lighter text-xs rounded flex flex-wrap items-center justify-center w-32 h-10">
                  <span class="w-full">Consent Declined</span>
                  <span class="w-full text-xxs">{{ applicant.declined_at | date }}</span>
                </button>
                <button v-else-if="applicant.consent === true" disabled="disabled" class="bg-green-lightest cursor-default bg-white text-grey border border-grey-light text-xs rounded flex flex-wrap items-center justify-center w-32 h-10">
                  <loader v-if="!clickable[index]" />
                  <span v-else class="pb-px">Consent Declined</span>
                </button>
                <button v-else :disabled="!clickable[index]" @click="showDeclineModal(index)" class="bg-white text-grey-darker border border-grey-dark text-xs rounded hover:bg-grey-lightest flex items-center justify-center w-32 h-10">
                  <loader v-if="!clickable[index]" />
                  <span v-else>Do Not Refer / Consent Declined</span>
                </button>
              </td>
              <td v-if="selectedOffice.enable_open_automated_referrals !== true" class="py-4 px-1 border-r-4 border-grey-light" :class="{'border-green-lighter': applicant.consent === true, 'border-red-lighter': applicant.consent === false}">
                <button v-if="applicant.consent === true" disabled="disabled" class="cursor-default bg-green text-white text-xs rounded flex flex-wrap items-center justify-center w-32 h-10">
                  <span class="w-full">Consent Given</span>
                  <span class="w-full text-xxs">{{ applicant.accepted_at | date }}</span>
                </button>
                <button v-else-if="applicant.consent === false" disabled="disabled" class="cursor-default bg-grey-light text-white text-xs rounded flex items-center justify-center w-32 h-10">
                  <loader v-if="!clickable[index]" />
                  <span v-else class="pb-px">Consent Given</span>
                </button>
                <button v-else :disabled="!clickable[index]" @click="showModal(index)" class="bg-white text-grey-darker border border-green text-xs rounded hover:bg-green hover:text-white flex items-center justify-center w-32 h-10">
                  <loader v-if="!clickable[index]" fill-color="white" />
                  <span v-else>Create Referral - Consent Given</span>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <modal
          width="70vw"
          v-if="showConsentModal !== false"
          @leave="showConsentModal = false; consentModalIndex = null"
          show-close-btn
        >
          <h3 class="mb-4 text-xl text-grey-darkest">Referral - Confirmation of Vendor Consent &amp; Acceptance of T&amp;C's</h3>
          <div class="">
            <p class="text-sm mb-4">By ticking the check box below you confirm that you have gained verbal or written consent to pass on <strong>{{ applicants[consentModalIndex].first_name }} {{ applicants[consentModalIndex].last_name }}'s</strong> personal data,
              regarding the potential sale of their home to a third party agent within {{ organisationName }}. By ticking the check box
              below you also confirm that you accept the referral system <strong><a @click="openTermsPage" class="underline cursor-pointer">Terms and Conditions</a></strong>.</p>
            <div class="border p-3 h-48 overflow-y-auto leading-normal page-content mb-6 text-sm" v-html="terms.sending"></div>
            <label class="text-sm font-bold uppercase">Notes for receiving agent:</label>
            <div class="py-2">
              <input v-model="applicants[consentModalIndex].already_on_market" id="on_market" type="checkbox" />
              <label for="on_market" class="text-sm">Property is already on the Market</label>
            </div>
            <textarea
              v-model="applicants[consentModalIndex].notes"
              rows="4"
              class="block w-full bg-white border hover:border-grey focus:border-grey-dark p-3 mt-2 mb-6 text-sm"
              placeholder="Type any notes in here..."
              ></textarea>
            <div class="flex w-full flex-row items-center text-center justify-center mb-6">
              <div>
                <input v-model="acceptTerms" id="accept_terms" type="checkbox" />
                <label for="accept_terms" class="text-sm">I have gained the vendor consent and accept the Terms &amp; Conditions</label>
              </div>
            </div>
            <div class="flex w-full flex-row items-center text-center justify-center">
              <button :disabled="!clickable[consentModalIndex] || !acceptTerms" @click="giveConsent(applicants[consentModalIndex], consentModalIndex)"
                class="text-white text-xs rounded flex items-center justify-center px-12 h-10"
                :class="!clickable[consentModalIndex] || !acceptTerms ? 'bg-grey hover:bg-grey cursor-default' : 'bg-green hover:bg-green-light'">
                <loader v-if="!clickable[consentModalIndex]" fill-color="white" />
                <span v-else>Send Referral</span>
              </button>
            </div>
          </div>
        </modal>
        <modal
          width="70vw"
          v-if="showDeclineConsentModal !== false"
          @leave="showDeclineConsentModal = false; consentModalIndex = null; declineReasonId = null"
          show-close-btn
        >
          <p class="text-sm mb-6">Are you sure you would like to decline to send on the potential referral? Please select one of the following reasons to help us understand why.</p>

          <label>Reason:</label>
          <select
            v-model="declineReasonId"
            class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 mb-6 h-10 text-sm">
            <option :value="null">[Please Select]</option>
            <option :value="1">Consent not given by Vendor</option>
            <option :value="2">Not an appropriate referral</option>
            <option :value="3">Within own Territory</option>
            <option :value="4">No referral required</option>
            <option :value="5">Other</option>
          </select>

          <button @click="declineConsent(applicants[declineConsentModalIndex], declineConsentModalIndex, declineReasonId)"
                :disabled="!declineReasonId"
                class="text-white text-xs rounded flex items-center justify-center px-12 h-10"
                :class="!clickable[declineConsentModalIndex] || !declineReasonId ? 'bg-grey hover:bg-grey cursor-default' : 'bg-black hover:bg-grey-dark'">
              <span>Confirm</span>
            </button>
        </modal>
        <loader v-if="reloading" />
        <paginator v-else :data="pagination" @change="pageChanged" />
      </div>
    </div>
    <deletion-modal
      v-if="confirmCrmProviders"
      :loading="cancellingCrmProviders"
      @confirmed="handleCrmProviderChange"
      @leave="confirmCrmProviders = null"
      @dismissed="confirmCrmProviders = null"
      confirm-label="Yes, Do it.">
      <p class="text-center">Are you sure you wish to apply these preset filters? Any filters you've already prepared will be overwritten.</p>
    </deletion-modal>
  </div>
</template>
<style type="scss" scoped>
.tab-margin-bottom {
    margin-bottom: -0.10rem;
}
</style>
<script>
import { v2Api } from '@/http'
import { mapActions, mapState } from 'vuex'
import { debounce } from 'lodash'
import { EventBus } from '@/EventBus'
import Loader from '../../components/Loader.vue'
import ReferralApplicantFilters from '@/components/Referrals/ReferralApplicantFilters.vue'
import DeletionModal from '@/components/Modals/DeletionModal'

export default {
  components: { Loader, ReferralApplicantFilters, DeletionModal },
  props: {
    page: Object
  },
  data () {
    return {
      applicants: [],
      agents: [],
      pagination: {
        current_page: 1,
        last_page: null
      },
      sorting: {},
      loading: true,
      reloading: false,
      clickable: [],
      showConsentModal: false,
      consentModalIndex: null,
      showDeclineConsentModal: false,
      declineConsentModalIndex: null,
      terms: null,
      acceptTerms: false,
      referralType: 'settings',
      actionRequiredReferral: [],
      archivedReferral: [],
      actionRequiredReferralCount: 0,
      declineReasonId: null,
      sellingFilterData: null,
      buyingFilterData: null,
      buyingReasonFilterData: null,
      sellingFilters: {},
      buyingFilters: {},
      buyingReasonFilters: {},
      crmProviders: {},
      confirmCrmProviders: null,
      cancellingCrmProviders: false,
      selectedCrmProvider: null,
      saving: false,
      showCrmProviderText: false
    }
  },
  computed: {
    ...mapState(['referralTerms', 'selectedOffice', 'system', 'potentialReferralCount']),
    organisationName () {
      if (this.selectedOffice.network_id === 2) {
        return 'The Guild organisation'
      }
      if ([7, 8].includes(this.selectedOffice.network_id)) {
        return 'The Fine & Country organisation'
      }
      return 'the organisation'
    },
    registrationLink () {
      return this.system.networks[0].grs_registration_url
    },
    selectedCrmProviderName () {
      const crm = this.crmProviders.find(provider => provider.id === this.selectedCrmProvider)
      return crm ? crm.name : null
    }
  },
  created () {
    this.loadContent()
  },
  methods: {
    ...mapActions(['displayToastNotification', 'fetchReferralTerms']),
    loadContent () {
      this.loading = true
      this.applicants = []
      this.agents = []
      this.terms = null
      this.pagination = {
        current_page: 1,
        last_page: null
      }
      if (this.selectedOffice.automated_referrals_permission !== true) {
        return
      }
      Promise.all([
        this.loadApplicants(),
        this.loadAgents(),
        this.loadReferralTerms(),
        this.loadFilters()
      ]).then(() => {
        this.loading = false
      })
    },
    loadApplicants () {
      return v2Api.get('referral-applicants', {
        params: {
          office_id: this.selectedOffice.id,
          page: this.pagination.current_page,
          active: 1,
          order_by: Object.keys(this.sorting).length > 0 ? Object.keys(this.sorting)[0] : null,
          sort: Object.values(this.sorting).length > 0 ? Object.values(this.sorting)[0] : null,
          referral_type: this.referralType,
          action_flag: this.referralType !== 'expired'
        }
      }).then((response) => {
        this.applicants = response.data.data
        this.pagination = response.data
        if (this.referralType === 'current' || this.referralType === 'settings') {
          this.actionRequiredReferralCount = this.pagination.total
        }
        this.reloading = false
        this.clickable = Array(this.applicants.length).fill(true)
        if (this.referralType === 'current' || this.referralType === 'settings') {
          this.$store.commit('updatePotentialReferralCount', this.applicants.length)
        }
      })
    },
    loadAgents () {
      return v2Api.get('referral-applicants/agents', {
        params: {
          office_id: this.selectedOffice.id
        }
      }).then((response) => {
        this.agents = response.data
      })
    },
    loadReferralTerms () {
      if (!this.referralTerms) {
        this.fetchReferralTerms().then(() => {
          this.terms = this.referralTerms
        })
      } else {
        this.terms = this.referralTerms
      }
    },
    showModal (index) {
      if (!this.applicants[index].referring_agent_id) {
        this.displayToastNotification({
          type: 'danger',
          icon: 'fa-exclamation-triangle',
          title: 'Error',
          info: 'Please select the referring agent'
        })
        return
      }
      this.showConsentModal = true
      this.consentModalIndex = index
    },
    showDeclineModal (index) {
      this.showDeclineConsentModal = true
      this.declineConsentModalIndex = index
      this.declineReasonId = null
    },
    giveConsent (applicant, index) {
      this.showConsentModal = false
      this.updateApplicant(applicant, index, 1)
    },
    declineConsent (applicant, index, declineReasonId) {
      this.showDeclineConsentModal = false
      this.updateApplicant(applicant, index, 0, declineReasonId)
    },
    updateApplicant: debounce(function (applicant, index, consent, declineReasonId = null) {
      this.clickable.splice(index, 1, false)
      v2Api.patch('/referral-applicants/' + applicant.id, {
        office_id: this.selectedOffice.id,
        consent: consent,
        referring_agent_id: applicant.referring_agent_id,
        notes: applicant.notes,
        already_on_market: applicant.already_on_market,
        decline_reason_id: declineReasonId || applicant.decline_reason_id
      }).then(response => {
        this.applicants.splice(index, 1, response.data)
        this.clickable.splice(index, 1, true)
        this.declineReasonId = null
      }, (error) => {
        let errors = error.response.data.errors
        let errorMessage = error.response.data.message
        if (Object.keys(errors)[0] && errors[Object.keys(errors)[0]]) {
          errorMessage += ' ' + errors[Object.keys(errors)[0]]
        }
        this.displayToastNotification({
          type: 'danger',
          icon: 'fa-exclamation-triangle',
          title: 'Error',
          info: errorMessage
        })
        this.clickable.splice(index, 1, true)
        this.declineReasonId = null
      })
    }, 500),
    sortBy (columnName) {
      if (this.sorting[columnName]) {
        let direction = this.sorting[columnName]
        if (direction === 'asc') {
          // it was sorting asc, so change it to sort desc
          let newSort = {}
          newSort[columnName] = 'desc'
          this.sorting = newSort
        } else {
          // it was sorting desc, don't sort with this field now
          this.sorting[columnName] = ''
          delete this.sorting[columnName]
        }
      } else {
        // it wasn't sorting, sort it asc
        let newSort = {}
        newSort[columnName] = 'asc'
        this.sorting = newSort
      }
      // re-fetch the data, sorted as requested
      this.reloading = true
      this.loadApplicants()
    },
    pageChanged (i) {
      this.pagination.current_page = i
      this.reloading = true
      this.loadApplicants()
    },
    openTermsPage () {
      EventBus.$emit('page:change-tab', 'terms')
    },
    openLink (link) {
      if (link.length < 1) {
        return
      }
      if (!link.startsWith('http', 0)) {
        link = 'https://' + link
      }
      window.open(link, '_blank')
    },
    loadFilters () {
      return v2Api.get('/referral-applicants/get-filters', {
        params: {
          office_id: this.selectedOffice.id
        }
      }).then((response) => {
        this.sellingFilters = response.data.selling_filters
        this.buyingFilters = response.data.buying_filters
        this.buyingReasonFilters = response.data.buying_reason_filters
        this.crmProviders = response.data.crm_providers
        let crmId = response.data.referral_applicant_crm_provider_id || null
        if (crmId) {
          this.showCrmProviderText = true
        }
        if (crmId === 5) {
          crmId = null
        }
        this.selectedCrmProvider = crmId
      })
    },
    saveSellingFilterData (data) {
      this.sellingFilterData = data
    },
    saveBuyingFilterData (data) {
      this.buyingFilterData = data
    },
    saveBuyingReasonFilterData (data) {
      this.buyingReasonFilterData = data
    },
    saveReferralApplicantFilters () {
      this.$refs.sellingRef.emitFilterData()
      this.$refs.buyingRef.emitFilterData()
      this.$refs.buyingReasonRef.emitFilterData()
      this.saving = true
      v2Api.post('/referral-applicants/store-filters', {
        office_id: this.selectedOffice.id,
        selling_filters: this.sellingFilterData,
        buying_filters: this.buyingFilterData,
        buying_reason_filters: this.buyingReasonFilterData
      }).then((response) => {
        this.loadFilters()
        this.loadApplicants()
        this.displayToastNotification({
          type: 'success',
          icon: 'fa-check',
          title: 'Successfully saved',
          info: 'Custom filter has been saved successfully.'
        })
        this.saving = false
      }, (error) => {
        this.displayToastNotification({
          type: 'danger',
          icon: 'fa-times-circle',
          title: 'Error',
          info: error.response.status === 500 ? 'Whoops! Looks like something went wrong. Changes have not been saved.' : error.response.data.message
        })
        this.saving = false
      })
    },
    handleCrmProviderChange () {
      v2Api.post('/referral-applicants/store-preset-filters', {
        office_id: this.selectedOffice.id,
        crm_provider_id: this.selectedCrmProvider
      }).then((response) => {
        this.loadFilters()
        this.loadApplicants()
        this.displayToastNotification({
          type: 'success',
          icon: 'fa-check',
          title: 'Successfully saved',
          info: 'Preset filters has been saved successfully.'
        })
      }, (error) => {
        this.displayToastNotification({
          type: 'danger',
          icon: 'fa-times-circle',
          title: 'Error',
          info: error.response.status === 500 ? 'Whoops! Looks like something went wrong. Changes have not been saved.' : error.response.data.message
        })
      })
      this.confirmCrmProviders = null
      this.showCrmProviderText = true
    },
    rePresentDropdown (event) {
      event.preventDefault()
      this.showCrmProviderText = false
    }
  },
  watch: {
    selectedOffice () {
      this.loadContent()
    },
    referralType () {
      this.loadContent()
    }
  }
}
</script>
